import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FileText, CheckCircle, XCircle, Calendar, User } from 'lucide-react';
import { supabase } from '../../lib/supabase';

export default function PublicVerifyDischarge() {
  const { summaryNo } = useParams<{ summaryNo: string }>();
  const [summary, setSummary] = useState<any>(null);
  const [admission, setAdmission] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    verifySummary();
  }, [summaryNo]);

  const verifySummary = async () => {
    if (!summaryNo) {
      setError('Invalid discharge summary number');
      setLoading(false);
      return;
    }

    try {
      const { data: summaryData, error: summaryError } = await supabase
        .from('discharge_summaries')
        .select(`
          *,
          admission:admissions(
            *,
            patient:patients(id, name, date_of_birth),
            doctor:doctors(id, name, specialization)
          )
        `)
        .eq('summary_no', summaryNo)
        .single();

      if (summaryError) {
        if (summaryError.code === 'PGRST116') {
          setError('Discharge summary not found');
        } else {
          throw summaryError;
        }
      } else {
        setSummary(summaryData);
        setAdmission(summaryData.admission);
      }
    } catch (err) {
      console.error('Verification error:', err);
      setError('Failed to verify discharge summary');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying discharge summary...</p>
        </div>
      </div>
    );
  }

  if (error || !summary) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Verification Failed</h1>
          <p className="text-gray-600 mb-6">{error || 'Discharge summary not found'}</p>
          <p className="text-sm text-gray-500">
            Please check the summary number and try again.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-green-50 p-6 border-b border-green-100">
            <div className="flex items-center gap-4">
              <CheckCircle className="w-12 h-12 text-green-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Verified Discharge Summary</h1>
                <p className="text-green-700">This discharge summary is authentic and valid</p>
              </div>
            </div>
          </div>

          <div className="p-8">
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Summary Information</h2>
              <dl className="grid grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Summary Number</dt>
                  <dd className="mt-1 text-sm text-gray-900">{summary.summary_no}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Discharge Date</dt>
                  <dd className="mt-1 text-sm text-gray-900 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    {new Date(summary.discharge_date).toLocaleDateString()}
                  </dd>
                </div>
              </dl>
            </div>

            <div className="mb-6 pb-6 border-b">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Patient Information</h2>
              <dl className="grid grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Patient Name</dt>
                  <dd className="mt-1 text-sm text-gray-900 flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-400" />
                    {admission?.patient?.name}
                  </dd>
                </div>
                {admission?.patient?.date_of_birth && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Date of Birth</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {new Date(admission.patient.date_of_birth).toLocaleDateString()}
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            <div className="mb-6 pb-6 border-b">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Admission Details</h2>
              <dl className="grid grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Admission Date</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {new Date(admission.admission_date).toLocaleDateString()}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Attending Doctor</dt>
                  <dd className="mt-1 text-sm text-gray-900">{admission?.doctor?.name}</dd>
                </div>
                {admission?.doctor?.specialization && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Specialization</dt>
                    <dd className="mt-1 text-sm text-gray-900">{admission.doctor.specialization}</dd>
                  </div>
                )}
              </dl>
            </div>

            {summary.final_diagnosis && (
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Final Diagnosis</h2>
                <p className="text-gray-700 whitespace-pre-wrap">{summary.final_diagnosis}</p>
              </div>
            )}

            {summary.follow_up_date && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-1">Follow-up Appointment</h3>
                <p className="text-sm text-gray-700">
                  Scheduled for: {new Date(summary.follow_up_date).toLocaleDateString()}
                </p>
              </div>
            )}
          </div>

          <div className="bg-gray-50 px-8 py-4 border-t">
            <div className="flex items-center justify-between text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                <span>Official Discharge Summary</span>
              </div>
              <span>Verified on {new Date().toLocaleDateString()}</span>
            </div>
          </div>
        </div>

        <div className="mt-6 text-center text-sm text-gray-500">
          <p>This is a publicly verifiable discharge summary.</p>
          <p>For full details, please contact the issuing healthcare facility.</p>
        </div>
      </div>
    </div>
  );
}
